import { Box, Link, Slider, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import ColorSwatchPicker from '~/components/Inputs/Color/ColorSwatchPicker';
import InlineToggle from '~/components/Inputs/InlineToggle';
import InterpretedTypeSelect from '~/components/Inputs/InterpretedTypeSelect';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import RegionResetButton from '../RegionResetButton';
import { WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS } from '../autoPreview';
import {
    STAGE_ROW_TOOLTIP_PLACEMENT,
    SmoothingToggleRow,
    ToggleNumberRow,
} from '~/components/toolbar/shared/StageToggleRows';
import { StructureSetId } from '~/domain/changes/ids';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { findExternalRoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    computeSuvThresholdStats,
    computeWindowRawMaxAt,
    suvThresholdRawFromPct,
} from '~/domain/patient/suvStats';
import { generateUniqueStructureNameFromList } from '~/domain/services/dicomProcessor';
import {
    VoiShape,
    VoxelBounds,
    VoxelSubRange,
    gridSubRange,
    gridVoxelBounds,
    subRangeEquals,
    subRangeIsEmpty,
} from '~/domain/services/imageService/gridGeometry';
import useViewportFocus from '~/hooks/useViewportFocus';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { petWindowMaxState, voiShapeState } from '~/state/atoms/toolsAtoms';
import type { PetWindowMax } from '~/state/atoms/toolsAtoms';
import { cm2ToMm2, formatMm3AsCm3, mm2ToCm2 } from '~/utils/units';
import { validateStructureName } from '~/utils/validation';
import {
    ActionButtons,
    getStructureColor,
    outputTargetBlocker,
    Label,
    OutputStructureSelect,
    StructureOption,
} from '../OperationFormControls';
import {
    isOverwriteUnpicked,
    NEW_OUTPUT_TARGET,
    OutputTarget,
    overwriteTarget,
    overwrittenRoiId,
} from '~/hooks/structure-set/outputTarget';
import {
    BodyContourMaskCache,
    bodyContourPreviewKey,
} from '../bodyContour/bodyContourParams';
import { resolveOwnOutputByName } from '../bodyContour/thresholdSegmentationPresets';
import { useBodyContourApplication } from '../bodyContour/useBodyContourApplication';
import { useBodyContourPreview } from '../bodyContour/useBodyContourPreview';
import { useVoiRegionRestriction } from '~/components/toolbar/shared/useVoiRegionRestriction';
import {
    SUV_ELIGIBILITY,
    SUV_THRESHOLD_DEFAULT_BOX_MM,
    SUV_THRESHOLD_DEFAULT_PCT,
    SUV_THRESHOLD_DEFAULT_SHAPE,
    SUV_THRESHOLD_MAX_PCT,
    SUV_THRESHOLD_MIN_PART_AREA_FLOOR_MM2,
    SUV_THRESHOLD_MIN_PART_AREA_MM2,
    SUV_THRESHOLD_MIN_PCT,
    SUV_THRESHOLD_OUTPUT,
    buildSuvThresholdParams,
    describeSuvGeneration,
} from './suvThresholdParams';
import { useReportBusy } from '~/components/toolbar/OperationsToolbar/useReportBusy';

interface SuvThresholdOperationContentProps {
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

/** Panel-session parameter state (one tool — no per-tab record). */
interface SuvPanelParams {
    thresholdPct: number;
    regionShape: VoiShape;
    fillHolesEnabled: boolean;
    smoothingEnabled: boolean;
    smoothingStrength: number;
    minPartSizeEnabled: boolean;
    minPartAreaMm2: number;
    keepLargest3dEnabled: boolean;
    keepLargest3dCount: number;
    color: DicomColor;
    customRoiName: string;
    interpretedType: RoiInterpretedType;
    outputTarget: OutputTarget;
}

function initPanelParams(): SuvPanelParams {
    return {
        thresholdPct: SUV_THRESHOLD_DEFAULT_PCT,
        regionShape: SUV_THRESHOLD_DEFAULT_SHAPE,
        // ON by default: a photopenic (necrotic) core is part of the
        // lesion the threshold has cut a hole in, not background.
        fillHolesEnabled: true,
        // OFF — PET is already PSF-smoothed, and smoothing shrinks small
        // MTVs, making the displayed stats diverge from the applied
        // structure.
        smoothingEnabled: false,
        smoothingStrength: 2,
        minPartSizeEnabled: false,
        minPartAreaMm2: SUV_THRESHOLD_MIN_PART_AREA_MM2,
        keepLargest3dEnabled: false,
        keepLargest3dCount: 1,
        color: { ...SUV_THRESHOLD_OUTPUT.color },
        customRoiName: SUV_THRESHOLD_OUTPUT.name,
        interpretedType: SUV_THRESHOLD_OUTPUT.defaultInterpretedType,
        outputTarget: NEW_OUTPUT_TARGET,
    };
}

/**
 * SUV Threshold (PET) panel — SR-WOR-H-27-3. Segments voxels at or
 * above a percentage of the SUVmax inside a MANDATORY region (the
 * classic fixed-%SUVmax method; 40 % default), on the shared
 * threshold-segmentation machinery: the %→raw conversion happens here
 * ({@link suvThresholdRawFromPct}), and the pipeline, mask cache,
 * preview lifecycle and output resolution are the CT tool's, reused
 * through their parameter seams.
 *
 * The region is not an optional restriction but the DOMAIN of SUVmax —
 * no region content, no threshold — so the region hook runs in mandatory
 * mode and the controls stay inert until the region holds uptake. It is
 * an inscribed ellipsoid by default and the bounds themselves on
 * request; the bounds are the handle geometry either way. The preview is
 * AUTOMATIC: a parameter change aborts the run in flight and schedules a
 * replacement, so the viewports always show what Apply would commit. The
 * displayed statistics describe the RAW thresholded mask, before any
 * cleanup stage — the stats heading says so, and every cleanup stage
 * defaults accordingly (only the 2D fill is on).
 *
 * The result goes to an explicitly chosen target: an existing structure,
 * which keeps its own name and type, or a new one the user names, types
 * and colours. The two are never conflated — a name the set already
 * holds is refused on the create path rather than resolved onto the
 * structure holding it.
 */
export default function SuvThresholdOperationContent({
    onClose,
    onPreventCloseChange,
}: SuvThresholdOperationContentProps) {
    const intl = useIntl();
    const { environment: activeEnvironment, structureSet: readModel } =
        useActiveEditTarget();
    // The resolver yields `undefined`; the consumers below take `null`.
    // Normalise once at the boundary.
    const environment = activeEnvironment ?? null;
    const image = activeEnvironment?.image?.parent;

    const [p, setP] = useState<SuvPanelParams>(initPanelParams);
    const patch = useCallback((update: Partial<SuvPanelParams>) => {
        setP((prev) => ({ ...prev, ...update }));
    }, []);

    const isEligible = !!image?.isSuvQuantified;
    const suvFactor = image?.suvFactor ?? null;

    // The mandatory region bounds, on the shared VOI overlay — the same
    // session-persistent per-image record as the CT tool (the two tools
    // can never be eligible for the same image, so sharing the record is
    // safe and keeps the box seams single). The seed is lesion-scaled
    // (a 5 cm cube), not the CT tool's half-the-image fraction — see
    // SUV_THRESHOLD_DEFAULT_BOX_MM.
    const { voiBounds, resetRegion } = useVoiRegionRestriction({
        consumer: 'threshold-segmentation',
        imageUid: image?.dicomUid,
        imageGrid: image?.grid,
        mandatory: true,
        seedExtentMm: SUV_THRESHOLD_DEFAULT_BOX_MM,
        // Open the box on what the user is looking at. Centred on the
        // image instead, a 5 cm cube lands mid-abdomen on a whole-body
        // PET and quotes an SUVmax from whatever happens to be there.
        seedCenterMm: environment?.planeIntersectionPoint?.coordinates,
    });

    // Snapped voxel window of the box — the SAME snapping the mask
    // pipeline applies, so SUVmax's domain and the segmented voxels agree
    // by construction.
    // Identity is held across pointer-moves that snap to the same voxels:
    // a drag emits a new bounds object per event, and the O(window) passes
    // below key on this. Snapping itself is arithmetic and stays cheap.
    // The region's EXACT bounds in voxel space — where its surface lies,
    // as opposed to the snapped window the passes iterate. The statistics
    // judge membership against these, the same bounds the segmentation
    // cuts at, so a voxel they count is a voxel the structure contains.
    const bounds = useMemo<VoxelBounds | null>(
        () =>
            image && voiBounds ? gridVoxelBounds(image.grid, voiBounds) : null,
        [image, voiBounds]
    );

    const windowRef = useRef<VoxelSubRange | null>(null);
    const window = useMemo(() => {
        const next =
            image && voiBounds ? gridSubRange(image.grid, voiBounds) : null;
        const previous = windowRef.current;
        if (
            next !== null &&
            previous !== null &&
            subRangeEquals(previous, next)
        ) {
            return previous;
        }
        windowRef.current = next;
        return next;
    }, [image, voiBounds]);

    // Box-dependent (threshold-independent): the raw maximum the % slider
    // is relative to, together with the voxel holding it — the viewport
    // marker's position, so the user can see WHERE the SUVmax the panel
    // quotes was measured.
    const windowMax = useMemo(
        () =>
            image && window && bounds && !subRangeIsEmpty(window)
                ? computeWindowRawMaxAt(
                      image.floatPixelData,
                      image.grid,
                      window,
                      bounds,
                      p.regionShape
                  )
                : null,
        [image, window, bounds, p.regionShape]
    );
    const rawMax = windowMax?.rawMax ?? null;
    // Zero/negative "maximum" means the box holds no uptake: a % of it is
    // meaningless (0 % of 0 would segment the whole box) — refuse rather
    // than threshold on nothing.
    const hasUptake = rawMax !== null && rawMax > 0;
    const rawThreshold = hasUptake
        ? suvThresholdRawFromPct(p.thresholdPct, rawMax)
        : null;

    // The SUVmax voxel's world-mm center TOGETHER with its SUVbw value,
    // published as one object for the persistent viewport marker (whose
    // label quotes the value) whenever the box holds uptake and cleared
    // otherwise (and on unmount, alongside the region hook's own
    // release). One memo, one atom write: position and value derive from
    // the same {@link windowMax} recompute, so a box drag moves the
    // marker with the recomputed maximum and the label can never show a
    // value measured at a stale position.
    const setMaxMarker = useSetRecoilState(petWindowMaxState);
    const windowMaxMarker = useMemo<PetWindowMax | null>(
        () =>
            image &&
            windowMax !== null &&
            windowMax.rawMax > 0 &&
            suvFactor !== null
                ? {
                      positionMm: image.grid.indexToVoxelCenterWorldSpace(
                          windowMax.flatIndex
                      ),
                      suvValue: windowMax.rawMax * suvFactor,
                  }
                : null,
        [image, windowMax, suvFactor]
    );
    const maxPositionMm = windowMaxMarker?.positionMm ?? null;
    useEffect(() => {
        setMaxMarker(windowMaxMarker);
    }, [windowMaxMarker, setMaxMarker]);
    useEffect(() => () => setMaxMarker(null), [setMaxMarker]);

    // The overlay draws whatever shape the panel is computing. Released
    // back to the box on unmount, like the marker above: this is the one
    // consumer that sets it, and a consumer with no shape control must
    // never inherit an ellipse from a panel that has closed.
    const setVoiShape = useSetRecoilState(voiShapeState);
    useEffect(() => {
        setVoiShape(p.regionShape);
    }, [p.regionShape, setVoiShape]);
    useEffect(() => () => setVoiShape('box'), [setVoiShape]);

    // Click-to-jump on the SUVmax stat: the generic point-focus (the dose
    // overlay's Max-label precedent), framing every tile of the measured
    // image, the one that draws the marker, on it so the user can verify
    // the measurement's origin up close.
    const { focusOnPoint } = useViewportFocus();
    const handleFocusOnMax = () => {
        if (maxPositionMm !== null) {
            focusOnPoint(maxPositionMm);
        }
    };

    // Threshold-dependent stats of the RAW mask (before cleanup stages).
    const stats = useMemo(
        () =>
            image &&
            window &&
            bounds &&
            rawThreshold !== null &&
            suvFactor !== null
                ? computeSuvThresholdStats(
                      image.floatPixelData,
                      image.grid,
                      window,
                      bounds,
                      p.regionShape,
                      rawThreshold,
                      suvFactor
                  )
                : null,
        [image, window, bounds, p.regionShape, rawThreshold, suvFactor]
    );

    const params = useMemo(
        () =>
            rawThreshold !== null && voiBounds
                ? buildSuvThresholdParams(
                      rawThreshold,
                      {
                          fillHolesEnabled: p.fillHolesEnabled,
                          smoothingEnabled: p.smoothingEnabled,
                          smoothingStrength: p.smoothingStrength,
                          minPartSizeEnabled: p.minPartSizeEnabled,
                          minPartAreaMm2: p.minPartAreaMm2,
                          keepLargest3dEnabled: p.keepLargest3dEnabled,
                          keepLargest3dCount: p.keepLargest3dCount,
                      },
                      voiBounds,
                      p.regionShape
                  )
                : null,
        [
            rawThreshold,
            voiBounds,
            p.regionShape,
            p.fillHolesEnabled,
            p.smoothingEnabled,
            p.smoothingStrength,
            p.minPartSizeEnabled,
            p.minPartAreaMm2,
            p.keepLargest3dEnabled,
            p.keepLargest3dCount,
        ]
    );

    // SUV-gated mask cache: its compute seam throws the typed
    // ImageNotSuvQuantifiedError — the fail-fast below the UI gating.
    const maskCache = useMemo(() => new BodyContourMaskCache('suv'), []);

    const {
        isPreviewing,
        previewStats,
        handlePreview,
        consumePreview,
        recolorPreview,
        abortInFlight: abortPreviewInFlight,
        cleanup: previewCleanup,
        updateView,
        heldKey,
    } = useBodyContourPreview({
        environment,
        image,
        maskCache,
        eligibility: SUV_ELIGIBILITY,
    });

    // Reseed on primary-image change: the threshold %, output target,
    // name and type belong to the previous scan, and the held preview /
    // cached mask were computed from it. The region record resets by
    // itself (it is stamped with the image uid).
    const outputSeededForSetRef = useRef<StructureSetId | null>(null);
    const imageUidRef = useRef(image?.dicomUid);
    useEffect(() => {
        if (image?.dicomUid !== imageUidRef.current) {
            imageUidRef.current = image?.dicomUid;
            outputSeededForSetRef.current = null;
            setP((prev) => ({
                ...prev,
                thresholdPct: SUV_THRESHOLD_DEFAULT_PCT,
                outputTarget: NEW_OUTPUT_TARGET,
                customRoiName: SUV_THRESHOLD_OUTPUT.name,
                interpretedType: SUV_THRESHOLD_OUTPUT.defaultInterpretedType,
            }));
            previewCleanup();
            maskCache.clear();
        }
    }, [image?.dicomUid, previewCleanup, maskCache]);

    const handleClose = useCallback(() => {
        previewCleanup();
        onClose();
    }, [previewCleanup, onClose]);

    // Generation description carries the % AND the resolved absolute SUV
    // (params.thresholdHu is the raw threshold; × factor = SUVbw). Built
    // from the same render as the params it is called with.
    const thresholdPct = p.thresholdPct;
    const regionShape = p.regionShape;
    const describeGeneration = useCallback(
        (appliedParams: { thresholdHu: number }) => {
            if (suvFactor === null) {
                // Unreachable behind the eligibility gates on render and
                // apply. Throw rather than fall back to 0: the description
                // is the applied structure's provenance, and recording
                // "0.0 g/ml" would state a threshold that was never used
                // as though it were the real one.
                throw new Error(
                    'describeGeneration: no SUV factor for a quantified PET image'
                );
            }
            return describeSuvGeneration(
                thresholdPct,
                appliedParams.thresholdHu * suvFactor,
                image?.suvQuantification ?? null,
                regionShape
            );
        },
        [thresholdPct, regionShape, suvFactor, image]
    );

    const apply = useBodyContourApplication({
        environment,
        image,
        onSuccess: handleClose,
        updateView,
        consumePreview,
        maskCache,
        eligibility: SUV_ELIGIBILITY,
        describeGeneration,
    });

    // One busy flag for close prevention and button gating.
    const isBusy = isPreviewing || apply.isProcessing;
    useReportBusy(isBusy, onPreventCloseChange);

    const existingNames = useMemo(
        () => readModel?.roiArray.map((r) => r.name) ?? [],
        [readModel]
    );

    // Result-target options — the by-name idiom of the threshold-seg
    // panel: approved structures stay listed (the select filters them),
    // point ROIs are removed (no contour geometry to receive).
    const availableStructures: StructureOption[] = useMemo(
        () =>
            readModel?.roiArray
                .filter((r) => !r.isPointRoi)
                .map((r) => ({
                    name: r.name,
                    roiId: r.roiId,
                    color: r.color,
                    isWriteProtected: !r.canModifyContours,
                })) ?? [],
        [readModel]
    );

    // Seed the output target once per structure set: an unapproved
    // structure already named SUVThreshold is the preselected overwrite
    // target, so re-running the tool replaces what it produced before,
    // and an approved or absent one leaves New Structure selected.
    //
    // The offered NEW name is made unique either way. The two controls
    // are independent — the user may switch the target to New Structure
    // at any time — so a name seeded from the existing structure would
    // hand the create path a name that is already taken.
    useEffect(() => {
        if (!readModel || outputSeededForSetRef.current === readModel.id) {
            return;
        }
        outputSeededForSetRef.current = readModel.id;
        const resolution = resolveOwnOutputByName(
            readModel.roiArray,
            SUV_THRESHOLD_OUTPUT.name
        );
        setP((prev) => ({
            ...prev,
            outputTarget:
                resolution.kind === 'overwrite'
                    ? overwriteTarget(resolution.roiId)
                    : NEW_OUTPUT_TARGET,
            customRoiName: generateUniqueStructureNameFromList(
                existingNames,
                SUV_THRESHOLD_OUTPUT.name
            ),
        }));
    }, [readModel, existingNames]);

    // New Structure CREATES: a name the set already holds is refused here
    // rather than resolved onto the structure holding it. Overwriting is
    // the other branch of the target control, chosen explicitly — a
    // create that silently redirected onto an existing structure would
    // replace contours the user never selected.
    const customNameError = useMemo(() => {
        if (p.outputTarget.kind === 'existing') {
            return null;
        }
        const [, error] = validateStructureName(p.customRoiName, existingNames);
        return error;
    }, [p.customRoiName, p.outputTarget, existingNames]);

    const currentPreviewKey = useMemo(
        () => (image && params ? bodyContourPreviewKey(image, params) : null),
        [image, params]
    );

    // Whether the held preview is the CURRENT parameters' result. Under
    // an automatic preview this is a transient state — the replacement is
    // already scheduled — so it gates the repaint below rather than
    // surfacing as a staleness warning.
    const isPreviewCurrent = heldKey !== null && heldKey === currentPreviewKey;

    // The on-screen preview follows the chosen color immediately — a
    // render-only repaint, only while the held preview is the current
    // parameters' result (the CT panel's rule).
    useEffect(() => {
        if (isPreviewCurrent) {
            recolorPreview(p.color);
        }
    }, [p.color, isPreviewCurrent, recolorPreview]);

    // Nothing to segment (box off the volume, no uptake, or 100 % above
    // everything) is not a previewable state: Preview is disabled below,
    // and a preview held from an earlier state is cleared outright — a
    // stale outline must never masquerade as this state's result, and
    // the stale hint could only point at a disabled button. The inline
    // hints below say why the viewport shows nothing.
    const canSegment =
        params !== null && stats !== null && stats.segmentedVoxelCount > 0;
    useEffect(() => {
        if (!canSegment) {
            previewCleanup();
        }
    }, [canSegment, previewCleanup]);

    // Whether an automatic run has already been STARTED for a key, so a
    // re-render cannot restart one, and a settled outcome — including an
    // empty result or a failure — is not retried forever.
    const attemptedKeyRef = useRef<string | null>(null);
    // Bumped when a run is deferred because another operation owns the
    // GPU, to re-offer it without narrating the contention.
    const [previewRetry, setPreviewRetry] = useState(0);
    const previewInputsRef = useRef({ params, color: p.color });
    previewInputsRef.current = { params, color: p.color };

    // The preview is AUTOMATIC: the viewports show the result of the
    // parameters as they stand, so what is on screen is always what Apply
    // would commit and there is nothing to mark outdated. A change aborts
    // the run in flight (usePreviewBase keeps the displayed preview up
    // until its replacement is ready) and schedules a new one; the delay
    // coalesces a threshold drag or a region drag into one compute.
    useEffect(() => {
        if (!canSegment || currentPreviewKey === null) {
            // Nothing to segment: the cleanup effect above has already
            // dropped any held preview, and a run would only produce a
            // refusal. The inline hints say why the viewport is empty.
            return;
        }
        if (attemptedKeyRef.current === currentPreviewKey) {
            return;
        }
        abortPreviewInFlight();
        const timer = setTimeout(() => {
            const inputs = previewInputsRef.current;
            if (!inputs.params) {
                return;
            }
            // dimsOtherStructures false: the result is a new separate
            // structure (the Bone/High-density rationale).
            void handlePreview(inputs.params, inputs.color, {
                dimsOtherStructures: false,
            }).then((settled) => {
                if (settled) {
                    attemptedKeyRef.current = currentPreviewKey;
                } else {
                    setPreviewRetry((n) => n + 1);
                }
            });
        }, WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timer);
    }, [
        canSegment,
        currentPreviewKey,
        previewRetry,
        abortPreviewInFlight,
        handlePreview,
    ]);

    const handleApply = useCallback(() => {
        if (!params || isOverwriteUnpicked(p.outputTarget)) {
            return;
        }
        // The interpreted type applies to a structure this operation
        // CREATES; an existing target keeps the type it already carries,
        // which is the user's clinical metadata and not this operation's
        // to rewrite. The apply path stamps it on creation only, so the
        // overwrite branch passes the same value harmlessly.
        const output = { interpretedType: p.interpretedType };
        const roiId = overwrittenRoiId(p.outputTarget);
        if (roiId !== null) {
            apply.handleApply(params, { output }, roiId, p.color);
            return;
        }
        apply.handleApply(
            params,
            { output },
            null,
            p.color,
            p.customRoiName.trim()
        );
    }, [
        apply,
        params,
        p.color,
        p.customRoiName,
        p.interpretedType,
        p.outputTarget,
    ]);

    if (!readModel) {
        return (
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="thresholdSeg.noStructureSet" />
            </Typography>
        );
    }
    if (!isEligible || suvFactor === null) {
        // Name why THIS image was refused. The generic sentence alone
        // sends a user whose series is BQML and decay-corrected off to
        // check tags that are already right, when the real reason is
        // something else (no attenuation correction, a GML series typed
        // other than BW, a degenerate range).
        const refusalReasons = image?.suvQuantification?.missingParams ?? [];
        return (
            <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="suvThreshold.notSuvPet" />
                </Typography>
                {refusalReasons.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        {refusalReasons.join('; ')}
                    </Typography>
                )}
            </Stack>
        );
    }

    // The stats row quotes the PUBLISHED marker value — one source, so
    // the panel number and the viewport label agree by construction.
    const suvMax = windowMaxMarker?.suvValue ?? null;
    const absoluteSuv = rawThreshold !== null ? rawThreshold * suvFactor : null;
    const outputRoiId = overwrittenRoiId(p.outputTarget);
    const overwriteTargetName =
        outputRoiId !== null
            ? (availableStructures.find((s) => s.roiId === outputRoiId)?.name ??
              null)
            : null;
    // Mirrors the domain's single-EXTERNAL invariant in the type control.
    const existingExternalName = findExternalRoiReadModel(readModel)?.name;
    const isCustomNameInvalid =
        p.outputTarget.kind === 'new' && !!customNameError;
    const targetBlocker = outputTargetBlocker(intl, p.outputTarget);
    // "—" for a stat with nothing to describe (empty selection); SUV to
    // 1 dp (house PET precision).
    const NO_VALUE = '—';

    return (
        <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="suvThreshold.description" />
            </Typography>

            {/* The region is the mandatory first step: it is already up
                (a 5 cm sphere seeded at the crosshair), this row tells
                the user it is theirs to position over the lesion. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                }}
            >
                <Box>
                    <Label>
                        <FormattedMessage id="suvThreshold.regionBox" />
                    </Label>
                    <Typography fontSize={10} color="text.secondary">
                        <FormattedMessage id="suvThreshold.regionBoxHint" />
                    </Typography>
                </Box>
                {/* The region is mandatory here, so it is always resettable
                    — and this is the only way to re-place it at the
                    crosshair without closing the panel. */}
                <RegionResetButton onReset={resetRegion} />
            </Box>

            <InlineToggle<VoiShape>
                labelId="suvThreshold.regionShape"
                tooltipId="suvThreshold.regionShapeTooltip"
                value={p.regionShape}
                options={[
                    {
                        value: 'ellipsoid',
                        labelId: 'suvThreshold.regionShape.ellipsoid',
                    },
                    { value: 'box', labelId: 'suvThreshold.regionShape.box' },
                ]}
                onChange={(regionShape) => patch({ regionShape })}
            />

            <Box>
                <Label>
                    <FormattedMessage id="suvThreshold.statsHeading" />
                </Label>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        columnGap: 1,
                        rowGap: 0.25,
                    }}
                    data-testid="suv-threshold-stats"
                >
                    <Typography fontSize={11}>
                        {/* With uptake the stat is a jump link to its own
                            measurement point — the dose overlay's
                            focus-on-max idiom. */}
                        {maxPositionMm !== null ? (
                            <Tooltip
                                title={intl.formatMessage({
                                    id: 'suvThreshold.focusOnMax',
                                })}
                                placement="top"
                            >
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={handleFocusOnMax}
                                    underline="hover"
                                    color="inherit"
                                    data-testid="suv-threshold-focus-max"
                                    aria-label={intl.formatMessage({
                                        id: 'suvThreshold.focusOnMax',
                                    })}
                                    sx={{
                                        fontSize: 'inherit',
                                        '&:hover': { color: 'primary.main' },
                                    }}
                                >
                                    <FormattedMessage
                                        id="suvThreshold.suvMax"
                                        values={{
                                            value:
                                                suvMax !== null
                                                    ? suvMax.toFixed(1)
                                                    : NO_VALUE,
                                        }}
                                    />
                                </Link>
                            </Tooltip>
                        ) : (
                            <FormattedMessage
                                id="suvThreshold.suvMax"
                                values={{ value: NO_VALUE }}
                            />
                        )}
                    </Typography>
                    <Typography fontSize={11}>
                        <FormattedMessage
                            id="suvThreshold.suvMean"
                            values={{
                                value:
                                    stats?.suvMean != null
                                        ? stats.suvMean.toFixed(1)
                                        : NO_VALUE,
                            }}
                        />
                    </Typography>
                </Box>
            </Box>

            <Box>
                <Label>
                    <FormattedMessage
                        id="suvThreshold.threshold"
                        values={{
                            pct: p.thresholdPct,
                            suv:
                                absoluteSuv !== null
                                    ? absoluteSuv.toFixed(1)
                                    : NO_VALUE,
                        }}
                    />
                </Label>
                <Slider
                    size="small"
                    value={p.thresholdPct}
                    min={SUV_THRESHOLD_MIN_PCT}
                    max={SUV_THRESHOLD_MAX_PCT}
                    step={1}
                    disabled={!hasUptake}
                    onChange={(_, value) =>
                        patch({ thresholdPct: value as number })
                    }
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `${v} %`}
                    aria-label={intl.formatMessage({
                        id: 'suvThreshold.thresholdAria',
                    })}
                    data-testid="suv-threshold-slider"
                />
            </Box>

            {!hasUptake && (
                <Typography fontSize={10} color="warning.main">
                    <FormattedMessage id="suvThreshold.noUptake" />
                </Typography>
            )}

            {/* Cleanup stages in pipeline order: fill → smoothing →
                min-size → keep 3D (keep 2D, final re-fill, margin and
                keep-inside are locked off — see buildSuvThresholdParams). */}
            <Box>
                <CompactToggle
                    labelId="thresholdSeg.fillHoles"
                    tooltipId="suvThreshold.fillHolesTooltip"
                    value={p.fillHolesEnabled}
                    onChange={(v) => patch({ fillHolesEnabled: v })}
                    dataTestId="suv-threshold-fill-holes"
                    tooltipPlacement={STAGE_ROW_TOOLTIP_PLACEMENT}
                />
            </Box>

            <SmoothingToggleRow
                labelId="thresholdSeg.smoothing"
                tooltipId="thresholdSeg.smoothingTooltip"
                enabled={p.smoothingEnabled}
                onEnabledChange={(v) => patch({ smoothingEnabled: v })}
                lightLabelId="thresholdSeg.smoothing.light"
                strongLabelId="thresholdSeg.smoothing.strong"
                strength={p.smoothingStrength}
                onStrengthChange={(v: number) =>
                    patch({ smoothingStrength: v })
                }
            />

            {/* cm² at the UI boundary, mm² in the domain — the CT panel's
                rule; step 0.1 cm² (not the CT tool's 0.5): PET-scale
                floors live an order of magnitude lower. */}
            <ToggleNumberRow
                labelId="thresholdSeg.minSize"
                tooltipId="thresholdSeg.minSizeTooltip"
                enabled={p.minPartSizeEnabled}
                onEnabledChange={(v) => patch({ minPartSizeEnabled: v })}
                value={mm2ToCm2(p.minPartAreaMm2)}
                onValueChange={(v) => patch({ minPartAreaMm2: cm2ToMm2(v) })}
                min={mm2ToCm2(SUV_THRESHOLD_MIN_PART_AREA_FLOOR_MM2)}
                step={0.1}
                precision={1}
                unit="cm²"
                dataTestId="suv-threshold-min-area"
            />

            <ToggleNumberRow
                labelId="thresholdSeg.keepLargest3d"
                tooltipId="suvThreshold.keepLargest3dTooltip"
                enabled={p.keepLargest3dEnabled}
                onEnabledChange={(v) => patch({ keepLargest3dEnabled: v })}
                value={p.keepLargest3dCount}
                onValueChange={(v) => patch({ keepLargest3dCount: v })}
                min={1}
                step={1}
                precision={0}
                snapToStep
                dataTestId="suv-threshold-keep-largest-3d-count"
            />

            <Stack spacing={1} sx={{ pt: 1 }}>
                {overwriteTargetName !== null && (
                    <Typography fontSize={10} fontStyle="italic">
                        <FormattedMessage
                            id="thresholdSeg.replacesExisting"
                            values={{ name: overwriteTargetName }}
                        />
                    </Typography>
                )}

                <OutputStructureSelect
                    label={intl.formatMessage({
                        id: 'thresholdSeg.outputNameLabel',
                    })}
                    target={p.outputTarget}
                    onTargetChange={(target) => patch({ outputTarget: target })}
                    inputColor={getStructureColor(p.color)}
                    inputRoiId={null}
                    availableStructures={availableStructures}
                    customRoiName={p.customRoiName}
                    setCustomRoiName={(v) => patch({ customRoiName: v })}
                    customNameError={customNameError}
                    defaultName={SUV_THRESHOLD_OUTPUT.name}
                    dataTestId="suv-threshold-output-select"
                />

                {/* Type and colour describe the structure the operation
                    writes, so they follow the target that decides which
                    structure that is. The type is offered only on the
                    create path: an existing target keeps its own. */}
                <Box display="flex" gap={1} alignItems="flex-end">
                    {p.outputTarget.kind === 'new' && (
                        <Box flexGrow={1}>
                            <Label>
                                <FormattedMessage id="structure.type" />
                            </Label>
                            <InterpretedTypeSelect
                                value={p.interpretedType}
                                onChange={(interpretedType) =>
                                    patch({ interpretedType })
                                }
                                existingExternalName={existingExternalName}
                                dataTestId="suv-threshold-type-select"
                            />
                        </Box>
                    )}
                    <Box>
                        <Label>
                            <FormattedMessage id="thresholdSeg.color" />
                        </Label>
                        <ColorSwatchPicker
                            color={p.color}
                            onChange={(c) => patch({ color: c })}
                            ariaLabel={intl.formatMessage({
                                id: 'thresholdSeg.color',
                            })}
                            dataTestId="suv-threshold-color-swatch"
                        />
                    </Box>
                </Box>
            </Stack>

            {isPreviewing && (
                <Typography fontSize={10} fontStyle="italic">
                    <FormattedMessage id="thresholdSeg.previewing" />
                </Typography>
            )}
            {/* Volume of the structure Apply would write — the only figure
                here that accounts for the cleanup stages, which the
                statistics above deliberately exclude. Suppressed while a
                new preview computes, so a stale number is never read as
                the incoming result's. */}
            {!isPreviewing && previewStats && (
                <Typography
                    fontSize={10}
                    fontStyle="italic"
                    data-testid="suv-threshold-preview-stats"
                >
                    <FormattedMessage
                        id="thresholdSeg.previewVolume"
                        values={{
                            volume: formatMm3AsCm3(previewStats.volumeMm3),
                        }}
                    />
                </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    {/* Apply is gated while a preview computes (GL-guard
                        race) and while there is nothing to segment — an
                        empty result must never replace the output's
                        contours. */}
                    <ActionButtons
                        label={intl.formatMessage({ id: 'thresholdSeg.apply' })}
                        onApply={handleApply}
                        isApplyDisabled={
                            isBusy ||
                            isCustomNameInvalid ||
                            targetBlocker !== undefined ||
                            !canSegment
                        }
                        applyDisabledReason={targetBlocker}
                    />
                </Box>
            </Box>
        </Stack>
    );
}
